import { useState, useMemo, useEffect } from 'react'
import {
  Trophy, Star, Gift, Zap, Crown, Medal,
  TrendingUp, Users, Target, Award, ChevronRight,
  Clock, CheckCircle, Lock, Flame, Diamond,
  ArrowUpRight, Search, Filter, MoreHorizontal, Plus,
} from 'lucide-react'

/* ─── static data ──────────────────────────────────────────────────────────── */
const TIER_CONFIG = [
  { name: 'Bronze',   min: 0,     max: 999,   color: '#CD7F32', glow: 'rgba(205,127,50,0.18)',  icon: Medal,   perks: ['5% cashback','Priority support','Monthly digest'] },
  { name: 'Silver',   min: 1000,  max: 4999,  color: '#A8B8C8', glow: 'rgba(168,184,200,0.18)', icon: Star,    perks: ['10% cashback','Early access','Dedicated account manager'] },
  { name: 'Gold',     min: 5000,  max: 14999, color: '#F59E0B', glow: 'rgba(245,158,11,0.18)',  icon: Crown,   perks: ['15% cashback','VIP events','Custom integrations'] },
  { name: 'Platinum', min: 15000, max: Infinity, color: '#A855F7', glow: 'rgba(168,85,247,0.18)', icon: Diamond, perks: ['20% cashback','White-glove onboarding','Dedicated CSM','SLA guarantee'] },
]

const FEATURED_REWARDS = [
  { id: 1,  name: '1 Month Pro Plan',    points: 500,   category: 'Subscription', claimed: false, popular: true,  icon: '🚀', tier: 'Bronze'   },
  { id: 2,  name: '$25 Account Credit',  points: 1000,  category: 'Credits',      claimed: false, popular: true,  icon: '💳', tier: 'Bronze'   },
  { id: 3,  name: 'Priority Queue',      points: 800,   category: 'Feature',      claimed: true,  popular: false, icon: '⚡', tier: 'Silver'  },
  { id: 4,  name: 'Custom Branding',     points: 2500,  category: 'Feature',      claimed: false, popular: false, icon: '🎨', tier: 'Silver'  },
  { id: 5,  name: '3 Month Pro Plan',    points: 1200,  category: 'Subscription', claimed: false, popular: true,  icon: '💎', tier: 'Silver'  },
  { id: 6,  name: 'API Rate Limit Boost',points: 3000,  category: 'Feature',      claimed: false, popular: false, icon: '📈', tier: 'Gold'    },
  { id: 7,  name: '$100 Account Credit', points: 4000,  category: 'Credits',      claimed: false, popular: false, icon: '💰', tier: 'Gold'    },
  { id: 8,  name: 'Dedicated CSM',       points: 10000, category: 'Service',      claimed: false, popular: false, icon: '🤝', tier: 'Platinum' },
]

const ACTIVITY = [
  { id: 1, action: 'Referred a user',         points: +250,  time: '2h ago',   icon: Users  },
  { id: 2, action: 'Subscription renewed',    points: +150,  time: '1d ago',   icon: CheckCircle },
  { id: 3, action: 'Redeemed: Priority Queue',points: -800,  time: '3d ago',   icon: Gift   },
  { id: 4, action: 'Profile completed',       points: +100,  time: '5d ago',   icon: Target },
  { id: 5, action: 'Referral bonus',          points: +500,  time: '1w ago',   icon: Zap    },
  { id: 6, action: 'Subscription renewed',    points: +150,  time: '2w ago',   icon: CheckCircle },
]

const TOP_EARNERS = [
  { rank: 1, name: 'Priya Sharma',    pts: 18420, tier: 'Platinum', avatar: 'PS' },
  { rank: 2, name: 'Arjun Mehta',     pts: 15800, tier: 'Platinum', avatar: 'AM' },
  { rank: 3, name: 'Kavya Reddy',     pts: 12300, tier: 'Gold',     avatar: 'KR' },
  { rank: 4, name: 'Rohan Gupta',     pts: 9750,  tier: 'Gold',     avatar: 'RG' },
  { rank: 5, name: 'Sneha Patel',     pts: 7200,  tier: 'Gold',     avatar: 'SP' },
]

const CHALLENGES = [
  { id: 1, title: 'Refer 3 Friends',     desc: 'Earn 750 bonus points',   progress: 2, total: 3, reward: 750,  icon: Users,  deadline: '4 days left'   },
  { id: 2, title: 'Complete Profile',    desc: 'Fill all profile fields',  progress: 8, total: 10,reward: 200,  icon: Target, deadline: 'Ongoing'       },
  { id: 3, title: 'Streak: 7 Logins',   desc: '500 points on completion', progress: 5, total: 7, reward: 500,  icon: Flame,  deadline: '2 days left'   },
  { id: 4, title: 'First Transaction',   desc: 'Make your first purchase', progress: 1, total: 1, reward: 300,  icon: Zap,    deadline: 'Completed'     },
]

const CATEGORIES = ['All', 'Subscription', 'Credits', 'Feature', 'Service']

/* ─── helpers ──────────────────────────────────────────────────────────────── */
function getTier(pts) {
  return TIER_CONFIG.find(t => pts >= t.min && pts <= t.max) || TIER_CONFIG[0]
}

function getNextTier(pts) {
  const idx = TIER_CONFIG.findIndex(t => pts >= t.min && pts <= t.max)
  return idx < TIER_CONFIG.length - 1 ? TIER_CONFIG[idx + 1] : null
}

function fmtPts(n) {
  return n >= 1000 ? (n / 1000).toFixed(1) + 'k' : String(n)
}

function tierColor(name) {
  return TIER_CONFIG.find(t => t.name === name)?.color || '#4F46E5'
}

/* ─── CSS ──────────────────────────────────────────────────────────────────── */
const STYLES = `
.rw-page { display:flex; flex-direction:column; gap:20px; padding:0 ; }

/* hero banner */
.rw-hero {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 28px 32px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 24px;
  align-items: center;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(16px);
}
.rw-hero::before {
  content:'';
  position:absolute; inset:0;
  background: radial-gradient(ellipse 60% 80% at 90% 50%, rgba(79,70,229,0.12) 0%, transparent 70%);
  pointer-events:none;
}
.rw-hero-left { display:flex; flex-direction:column; gap:10px; }
.rw-hero-badge {
  display:inline-flex; align-items:center; gap:6px;
  font-size:11px; font-weight:700; letter-spacing:.08em;
  padding:4px 12px; border-radius:var(--radius-full);
  width:fit-content;
  text-transform:uppercase;
}
.rw-hero-name { font-size:1.5rem; font-weight:800; color:var(--text1); letter-spacing:-0.03em; }
.rw-hero-pts  { font-size:2.5rem; font-weight:900; color:var(--text1); letter-spacing:-0.05em; line-height:1; }
.rw-hero-pts span { font-size:1rem; font-weight:500; color:var(--text3); margin-left:6px; }
.rw-progress-wrap { margin-top:4px; }
.rw-progress-label { display:flex; justify-content:space-between; font-size:11px; color:var(--text3); margin-bottom:6px; }
.rw-progress-bar   { height:6px; border-radius:var(--radius-full); background:var(--border-subtle); overflow:hidden; }
.rw-progress-fill  { height:100%; border-radius:var(--radius-full); transition:width 1s cubic-bezier(.4,0,.2,1); }
.rw-hero-right { display:flex; flex-direction:column; align-items:center; gap:8px; }
.rw-tier-ring {
  width:100px; height:100px; border-radius:50%;
  display:flex; align-items:center; justify-content:center; flex-direction:column; gap:4px;
  border:3px solid; position:relative;
}
.rw-tier-ring-label { font-size:10px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; }
.rw-tier-name       { font-size:12px; font-weight:700; color:var(--text2); }

/* stats row */
.rw-stats { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; }

/* section header */
.rw-sec-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; flex-wrap:wrap; gap:8px; }
.rw-sec-title { font-size:13px; font-weight:700; color:var(--text1); display:flex; align-items:center; gap:7px; }
.rw-sec-link  { font-size:11px; font-weight:600; color:var(--accent); background:none; border:none; cursor:pointer; display:flex; align-items:center; gap:3px; transition:color .15s; }
.rw-sec-link:hover { color:var(--accent-hover); }

/* rewards grid */
.rw-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; }
.rw-reward-card {
  background:var(--bg-card); border:1px solid var(--border);
  border-radius:var(--radius-lg); padding:16px; position:relative;
  transition:transform .15s, border-color .15s, box-shadow .15s;
  backdrop-filter:blur(16px); cursor:default;
}
.rw-reward-card:hover { transform:translateY(-2px); border-color:var(--border-active); box-shadow:0 8px 24px rgba(0,0,0,.25); }
.rw-reward-card.claimed { opacity:.55; pointer-events:none; }
.rw-reward-card.locked  { opacity:.45; }
.rw-reward-icon { font-size:28px; margin-bottom:10px; }
.rw-reward-name { font-size:13px; font-weight:700; color:var(--text1); margin-bottom:4px; }
.rw-reward-cat  { font-size:10px; font-weight:600; color:var(--text3); text-transform:uppercase; letter-spacing:.07em; margin-bottom:12px; }
.rw-reward-pts  { font-size:12px; font-weight:800; color:var(--accent); }
.rw-reward-pts-lbl { font-size:10px; color:var(--text3); }
.rw-reward-footer { display:flex; align-items:center; justify-content:space-between; margin-top:12px; padding-top:10px; border-top:1px solid var(--border-subtle); }
.rw-popular-badge { position:absolute; top:10px; right:10px; background:var(--color-amber-glow); color:var(--color-amber); border:1px solid rgba(245,158,11,.25); font-size:9px; font-weight:700; padding:2px 7px; border-radius:var(--radius-full); letter-spacing:.06em; text-transform:uppercase; }
.rw-locked-badge  { position:absolute; top:10px; right:10px; background:rgba(255,255,255,.05); color:var(--text3); border:1px solid var(--border); font-size:9px; font-weight:600; padding:2px 7px; border-radius:var(--radius-full); display:flex; align-items:center; gap:3px; }
.rw-claimed-overlay { position:absolute; top:10px; right:10px; background:var(--color-green-glow); color:var(--color-green); border:1px solid rgba(16,185,129,.25); font-size:9px; font-weight:700; padding:2px 7px; border-radius:var(--radius-full); display:flex; align-items:center; gap:3px; text-transform:uppercase; letter-spacing:.06em; }

/* redeem btn */
.rw-redeem-btn {
  background:var(--accent); color:#fff; border:none;
  border-radius:var(--radius-sm); padding:5px 14px; font-size:11px; font-weight:700;
  cursor:pointer; transition:all .15s; font-family:var(--font-body);
}
.rw-redeem-btn:hover { background:var(--accent-hover); transform:translateY(-1px); }
.rw-redeem-btn:disabled { background:var(--bg-raised); color:var(--text3); cursor:not-allowed; transform:none; }

/* filter tabs */
.rw-filter-row { display:flex; gap:6px; flex-wrap:wrap; }
.rw-filter-btn {
  font-size:11px; font-weight:600; padding:5px 14px; border-radius:var(--radius-full);
  border:1px solid var(--border); background:transparent; color:var(--text3);
  cursor:pointer; transition:all .15s; font-family:var(--font-body);
}
.rw-filter-btn:hover  { color:var(--text2); border-color:var(--border-active); }
.rw-filter-btn.active { background:var(--accent); color:#fff; border-color:var(--accent); }

/* two col layout */
.rw-two-col { display:grid; grid-template-columns:1.35fr 1fr; gap:16px; align-items:start; }

/* activity */
.rw-activity-item { display:flex; align-items:center; gap:12px; padding:10px 0; border-bottom:1px solid var(--border-subtle); }
.rw-activity-item:last-child { border-bottom:none; }
.rw-act-icon { width:32px; height:32px; border-radius:var(--radius-sm); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.rw-act-name { font-size:12px; font-weight:600; color:var(--text1); }
.rw-act-time { font-size:10px; color:var(--text3); margin-top:1px; }
.rw-act-pts  { font-size:12px; font-weight:700; margin-left:auto; white-space:nowrap; }

/* leaderboard */
.rw-leader-row { display:flex; align-items:center; gap:12px; padding:9px 0; border-bottom:1px solid var(--border-subtle); }
.rw-leader-row:last-child { border-bottom:none; }
.rw-leader-rank { font-size:11px; font-weight:800; color:var(--text3); width:20px; text-align:center; flex-shrink:0; }
.rw-leader-rank.top { color:var(--color-amber); }
.rw-avatar { width:30px; height:30px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; flex-shrink:0; background:var(--accent-muted); color:var(--accent-hover); }
.rw-leader-name { font-size:12px; font-weight:600; color:var(--text1); }
.rw-leader-pts  { font-size:11px; font-weight:700; color:var(--text2); margin-left:auto; }

/* challenges */
.rw-challenge-item { background:var(--bg-raised); border:1px solid var(--border); border-radius:var(--radius-md); padding:14px; margin-bottom:8px; transition:border-color .15s; }
.rw-challenge-item:last-child { margin-bottom:0; }
.rw-challenge-item:hover { border-color:var(--border-active); }
.rw-ch-head { display:flex; align-items:flex-start; justify-content:space-between; gap:8px; margin-bottom:8px; }
.rw-ch-icon { width:30px; height:30px; border-radius:var(--radius-sm); background:var(--accent-glow-sm); color:var(--accent); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.rw-ch-title { font-size:12px; font-weight:700; color:var(--text1); }
.rw-ch-desc  { font-size:10px; color:var(--text3); margin-top:1px; }
.rw-ch-reward { font-size:11px; font-weight:700; color:var(--color-amber); white-space:nowrap; }
.rw-ch-bar   { height:4px; background:var(--border-subtle); border-radius:var(--radius-full); overflow:hidden; margin-top:8px; }
.rw-ch-fill  { height:100%; background:var(--accent); border-radius:var(--radius-full); transition:width .8s cubic-bezier(.4,0,.2,1); }
.rw-ch-meta  { display:flex; justify-content:space-between; font-size:10px; color:var(--text3); margin-top:5px; }

/* tiers */
.rw-tier-row { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; }
.rw-tier-card { background:var(--bg-card); border:1px solid var(--border); border-radius:var(--radius-lg); padding:20px 16px; text-align:center; position:relative; transition:transform .15s, border-color .15s; backdrop-filter:blur(16px); }
.rw-tier-card:hover { transform:translateY(-2px); border-color:var(--border-active); }
.rw-tier-card.active { border-width:2px; }
.rw-tier-card-icon { width:44px; height:44px; border-radius:50%; display:flex; align-items:center; justify-content:center; margin:0 auto 10px; }
.rw-tier-card-name { font-size:14px; font-weight:800; margin-bottom:2px; }
.rw-tier-card-range { font-size:10px; color:var(--text3); margin-bottom:12px; }
.rw-tier-perk { font-size:10px; color:var(--text2); padding:3px 0; border-bottom:1px solid var(--border-subtle); text-align:left; display:flex; align-items:center; gap:5px; }
.rw-tier-perk:last-child { border-bottom:none; }
.rw-tier-active-badge { position:absolute; top:-1px; left:50%; transform:translateX(-50%); background:inherit; font-size:9px; font-weight:700; padding:2px 10px; border-radius:0 0 var(--radius-sm) var(--radius-sm); border:1px solid; border-top:none; text-transform:uppercase; letter-spacing:.08em; }

/* search */
.rw-search-wrap { position:relative; flex:1; max-width:260px; }
.rw-search-icon { position:absolute; left:10px; top:50%; transform:translateY(-50%); color:var(--text3); pointer-events:none; }
.rw-search-inp  { padding-left:32px !important; }

/* ── responsive ── */
@media(max-width:1100px) {
  .rw-grid { grid-template-columns:repeat(3,1fr); }
  .rw-tier-row { grid-template-columns:repeat(2,1fr); }
}
@media(max-width:900px) {
  .rw-stats { grid-template-columns:repeat(2,1fr); }
  .rw-two-col { grid-template-columns:1fr; }
  .rw-grid { grid-template-columns:repeat(2,1fr); }
  .rw-hero { grid-template-columns:1fr; }
  .rw-hero-right { flex-direction:row; justify-content:flex-start; }
}
@media(max-width:640px) {
  .rw-page { padding:0 14px 32px; }
  .rw-grid { grid-template-columns:repeat(2,1fr); }
  .rw-tier-row { grid-template-columns:repeat(2,1fr); }
  .rw-hero { padding:20px; }
  .rw-hero-pts { font-size:2rem; }
  .rw-sec-head { flex-direction:column; align-items:flex-start; }
}
@media(max-width:420px) {
  .rw-grid { grid-template-columns:1fr; }
  .rw-stats { grid-template-columns:repeat(2,1fr); }
  .rw-tier-row { grid-template-columns:1fr; }
  .rw-page { padding:0 10px 28px; }
  .rw-hero-right { display:none; }
  .rw-search-wrap { max-width:100%; }
}
`

function useStyles() {
  useEffect(() => {
    const id = 'rw-styles'
    if (document.getElementById(id)) return
    const el = document.createElement('style')
    el.id = id; el.textContent = STYLES
    document.head.appendChild(el)
    return () => document.getElementById(id)?.remove()
  }, [])
}

/* ─── Sub-components ────────────────────────────────────────────────────────── */

function StatCard({ label, value, icon: Icon, color, sub }) {
  return (
    <div className="stat-card" style={{ '--sc-color': color }}>
      <div className="stat-card-header">
        <div className="stat-card-icon"><Icon size={16}/></div>
        <div className="stat-card-trend" style={{ background:'transparent', border:'none', color:'var(--text3)', fontSize:10 }}>{sub}</div>
      </div>
      <div className="stat-card-value">{value}</div>
      <div className="stat-card-label">{label}</div>
    </div>
  )
}

function RewardCard({ reward, userPoints, onRedeem }) {
  const tier = TIER_CONFIG.find(t => t.name === reward.tier)
  const tierIdx = TIER_CONFIG.findIndex(t => t.name === reward.tier)
  const userTierIdx = TIER_CONFIG.findIndex(t => userPoints >= t.min && userPoints <= t.max)
  const isLocked = userTierIdx < tierIdx
  const canAfford = userPoints >= reward.points

  return (
    <div className={`rw-reward-card${reward.claimed ? ' claimed' : ''}${isLocked ? ' locked' : ''}`}>
      {reward.popular && !reward.claimed && !isLocked && (
        <div className="rw-popular-badge">🔥 Popular</div>
      )}
      {isLocked && (
        <div className="rw-locked-badge"><Lock size={8}/>{reward.tier}</div>
      )}
      {reward.claimed && (
        <div className="rw-claimed-overlay"><CheckCircle size={9}/>Claimed</div>
      )}
      <div className="rw-reward-icon">{reward.icon}</div>
      <div className="rw-reward-name">{reward.name}</div>
      <div className="rw-reward-cat">{reward.category}</div>
      <div className="rw-reward-footer">
        <div>
          <div className="rw-reward-pts">{fmtPts(reward.points)}</div>
          <div className="rw-reward-pts-lbl">points</div>
        </div>
        <button
          className="rw-redeem-btn"
          disabled={reward.claimed || isLocked || !canAfford}
          onClick={() => onRedeem(reward)}
        >
          {reward.claimed ? 'Claimed' : isLocked ? 'Locked' : !canAfford ? 'Need more' : 'Redeem'}
        </button>
      </div>
    </div>
  )
}

function ActivityItem({ item }) {
  const { icon: Icon, action, points, time } = item
  const isPositive = points > 0
  return (
    <div className="rw-activity-item">
      <div className="rw-act-icon" style={{
        background: isPositive ? 'var(--color-green-glow)' : 'var(--color-red-glow)',
        color: isPositive ? 'var(--color-green)' : 'var(--color-red)',
      }}>
        <Icon size={14}/>
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div className="rw-act-name">{action}</div>
        <div className="rw-act-time">{time}</div>
      </div>
      <div className="rw-act-pts" style={{ color: isPositive ? 'var(--color-green)' : 'var(--color-red)' }}>
        {isPositive ? '+' : ''}{points.toLocaleString()}
      </div>
    </div>
  )
}

function LeaderRow({ entry }) {
  return (
    <div className="rw-leader-row">
      <div className={`rw-leader-rank${entry.rank <= 3 ? ' top' : ''}`}>
        {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : `#${entry.rank}`}
      </div>
      <div className="rw-avatar" style={{ background: `${tierColor(entry.tier)}22`, color: tierColor(entry.tier) }}>
        {entry.avatar}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div className="rw-leader-name">{entry.name}</div>
        <div style={{ fontSize:9, fontWeight:600, color: tierColor(entry.tier), textTransform:'uppercase', letterSpacing:'.06em' }}>{entry.tier}</div>
      </div>
      <div className="rw-leader-pts">{fmtPts(entry.pts)} pts</div>
    </div>
  )
}

function ChallengeItem({ ch }) {
  const pct = Math.round((ch.progress / ch.total) * 100)
  const isDone = ch.progress >= ch.total
  return (
    <div className="rw-challenge-item">
      <div className="rw-ch-head">
        <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
          <div className="rw-ch-icon" style={isDone ? { background:'var(--color-green-glow)', color:'var(--color-green)' } : {}}>
            {isDone ? <CheckCircle size={15}/> : <ch.icon size={15}/>}
          </div>
          <div>
            <div className="rw-ch-title">{ch.title}</div>
            <div className="rw-ch-desc">{ch.desc}</div>
          </div>
        </div>
        <div className="rw-ch-reward">+{ch.reward} pts</div>
      </div>
      <div className="rw-ch-bar">
        <div className="rw-ch-fill" style={{
          width:`${pct}%`,
          background: isDone ? 'var(--color-green)' : 'var(--accent)',
        }}/>
      </div>
      <div className="rw-ch-meta">
        <span>{ch.progress}/{ch.total} completed</span>
        <span style={{ color: isDone ? 'var(--color-green)' : 'var(--text3)' }}>{ch.deadline}</span>
      </div>
    </div>
  )
}

function TierCard({ tier, isCurrent }) {
  const Icon = tier.icon
  return (
    <div className="rw-tier-card" style={isCurrent ? {
      borderColor: tier.color,
      boxShadow: `0 0 0 1px ${tier.color}40, 0 8px 24px ${tier.glow}`,
    } : {}}>
      {isCurrent && (
        <div className="rw-tier-active-badge" style={{ color: tier.color, borderColor: tier.color }}>
          Current
        </div>
      )}
      <div className="rw-tier-card-icon" style={{ background: tier.glow }}>
        <Icon size={22} color={tier.color}/>
      </div>
      <div className="rw-tier-card-name" style={{ color: tier.color }}>{tier.name}</div>
      <div className="rw-tier-card-range">
        {tier.max === Infinity ? `${fmtPts(tier.min)}+ pts` : `${fmtPts(tier.min)} – ${fmtPts(tier.max)} pts`}
      </div>
      <div>
        {tier.perks.map((p, i) => (
          <div key={i} className="rw-tier-perk">
            <CheckCircle size={10} style={{ color: tier.color, flexShrink:0 }}/>{p}
          </div>
        ))}
      </div>
    </div>
  )
}

/* ─── Main Page ─────────────────────────────────────────────────────────────── */
export default function RewardsPage() {
  useStyles()

  const USER_POINTS = 6800
  const USER_NAME   = 'Admin User'

  const currentTier = getTier(USER_POINTS)
  const nextTier    = getNextTier(USER_POINTS)
  const ptsToNext   = nextTier ? nextTier.min - USER_POINTS : 0
  const progressPct = nextTier
    ? Math.round(((USER_POINTS - currentTier.min) / (nextTier.min - currentTier.min)) * 100)
    : 100

  const [category,  setCategory]  = useState('All')
  const [search,    setSearch]    = useState('')
  const [rewards,   setRewards]   = useState(FEATURED_REWARDS)
  const [userPts,   setUserPts]   = useState(USER_POINTS)
  const [toast,     setToast]     = useState(null)

  const filtered = useMemo(() =>
    rewards.filter(r =>
      (category === 'All' || r.category === category) &&
      r.name.toLowerCase().includes(search.toLowerCase())
    ), [rewards, category, search])

  function handleRedeem(reward) {
    if (userPts < reward.points) return
    setRewards(prev => prev.map(r => r.id === reward.id ? { ...r, claimed: true } : r))
    setUserPts(p => p - reward.points)
    setToast(`✅ Redeemed: ${reward.name}`)
    setTimeout(() => setToast(null), 3000)
  }

  const curTier = getTier(userPts)

  return (
    <div>
      {/* Top bar */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 0 20px 0', flexWrap:'wrap', gap:10 }}>
        <div>
          <div className="page-title">Rewards</div>
          <div style={{ fontSize:11, color:'var(--text3)', marginTop:'1rem' }}>Track points, unlock rewards and climb tiers</div>
        </div>
        <button className="btn btn-primary btn-sm" style={{ display:'flex', alignItems:'center', gap:6 }}>
          <Plus size={13}/> Add Points
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position:'fixed', bottom:24, right:24, zIndex:9999,
          background:'var(--bg-raised)', border:'1px solid var(--color-green)', color:'var(--color-green)',
          borderRadius:'var(--radius-md)', padding:'10px 18px', fontSize:13, fontWeight:600,
          boxShadow:'var(--shadow-lg)', animation:'soft-rise .25s ease',
        }}>
          {toast}
        </div>
      )}

      <div className="rw-page">

        {/* ── Hero banner ── */}
        <div className="rw-hero">
          <div className="rw-hero-left">
            <div className="rw-hero-badge" style={{ background: curTier.glow, color: curTier.color, border:`1px solid ${curTier.color}40` }}>
              <curTier.icon size={11}/>{curTier.name} Member
            </div>
            <div className="rw-hero-name">Welcome back, {USER_NAME}</div>
            <div className="rw-hero-pts">
              {userPts.toLocaleString()}<span>total points</span>
            </div>
            {nextTier && (
              <div className="rw-progress-wrap">
                <div className="rw-progress-label">
                  <span>{curTier.name}</span>
                  <span>{ptsToNext.toLocaleString()} pts to {nextTier.name}</span>
                </div>
                <div className="rw-progress-bar">
                  <div className="rw-progress-fill" style={{ width:`${progressPct}%`, background: curTier.color }}/>
                </div>
              </div>
            )}
            {!nextTier && (
              <div style={{ fontSize:12, color:'var(--color-purple)', fontWeight:600, display:'flex', alignItems:'center', gap:5 }}>
                <Diamond size={13}/> Max tier reached — Platinum Member
              </div>
            )}
          </div>
          <div className="rw-hero-right">
            <div className="rw-tier-ring" style={{ borderColor: curTier.color, boxShadow: `0 0 20px ${curTier.glow}` }}>
              <curTier.icon size={28} color={curTier.color}/>
              <div className="rw-tier-ring-label" style={{ color: curTier.color }}>{curTier.name}</div>
            </div>
            <div className="rw-tier-name">{progressPct}% to {nextTier?.name || 'Max'}</div>
          </div>
        </div>

        {/* ── Stat cards ── */}
        <div className="rw-stats">
          <StatCard label="Total Points Earned" value="14,250"   color="var(--accent)"        icon={Star}      sub="All time"/>
          <StatCard label="Points Redeemed"      value="7,450"   color="var(--color-amber)"   icon={Gift}      sub="All time"/>
          <StatCard label="Active Challenges"    value="3"       color="var(--color-cyan)"    icon={Target}    sub="2 expiring soon"/>
          <StatCard label="Referrals Made"       value="8"       color="var(--color-green)"   icon={Users}     sub="+2,000 pts earned"/>
        </div>

        {/* ── Rewards catalog ── */}
        <div>
          <div className="rw-sec-head">
            <div className="rw-sec-title">
              <Gift size={15} color="var(--color-amber)"/>Rewards Catalog
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
              <div className="rw-search-wrap">
                <Search size={13} className="rw-search-icon"/>
                <input
                  className="rw-search-inp"
                  style={{ background:'var(--bg-raised)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text1)', fontSize:12, padding:'6px 10px 6px 32px', outline:'none', width:'100%', fontFamily:'var(--font-body)' }}
                  placeholder="Search rewards..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Category filters */}
          <div className="rw-filter-row" style={{ marginBottom:14 }}>
            {CATEGORIES.map(c => (
              <button key={c} className={`rw-filter-btn${category===c?' active':''}`} onClick={() => setCategory(c)}>{c}</button>
            ))}
          </div>

          <div className="rw-grid">
            {filtered.map(r => (
              <RewardCard key={r.id} reward={r} userPoints={userPts} onRedeem={handleRedeem}/>
            ))}
            {filtered.length === 0 && (
              <div style={{ gridColumn:'1/-1', textAlign:'center', padding:'40px 0', color:'var(--text3)', fontSize:13 }}>
                <Gift size={32} style={{ display:'block', margin:'0 auto 10px', opacity:.3 }}/>
                No rewards found
              </div>
            )}
          </div>
        </div>

        {/* ── Two-col: Challenges + Activity ── */}
        <div className="rw-two-col">
          {/* Challenges */}
          <div className="card">
            <div className="rw-sec-head">
              <div className="rw-sec-title"><Flame size={15} color="var(--color-red)"/>Active Challenges</div>
              <button className="rw-sec-link">View all <ChevronRight size={11}/></button>
            </div>
            {CHALLENGES.map(ch => <ChallengeItem key={ch.id} ch={ch}/>)}
          </div>

          {/* Activity */}
          <div className="card">
            <div className="rw-sec-head">
              <div className="rw-sec-title"><TrendingUp size={15} color="var(--color-cyan)"/>Recent Activity</div>
              <button className="rw-sec-link">See all <ChevronRight size={11}/></button>
            </div>
            {ACTIVITY.map(a => <ActivityItem key={a.id} item={a}/>)}
          </div>
        </div>

        {/* ── Two-col: Leaderboard + Tiers ── */}
        <div className="rw-two-col">
          {/* Leaderboard */}
          <div className="card" style={{ '--sc-color': 'var(--color-amber)' }}>
            <div className="rw-sec-head">
              <div className="rw-sec-title"><Trophy size={15} color="var(--color-amber)"/>Top Earners</div>
              <button className="rw-sec-link">Full leaderboard <ChevronRight size={11}/></button>
            </div>
            {TOP_EARNERS.map(e => <LeaderRow key={e.rank} entry={e}/>)}
          </div>

          {/* Tiers overview compact */}
          <div className="card">
            <div className="rw-sec-head">
              <div className="rw-sec-title"><Award size={15} color="var(--accent)"/>Your Tier Benefits</div>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {curTier.perks.map((p, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', background:'var(--bg-raised)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border-subtle)' }}>
                  <CheckCircle size={13} color={curTier.color}/>
                  <span style={{ fontSize:12, color:'var(--text2)', fontWeight:500 }}>{p}</span>
                </div>
              ))}
              {nextTier && (
                <div style={{ padding:'10px 12px', background:'var(--bg-hover)', borderRadius:'var(--radius-sm)', border:`1px dashed ${curTier.color}50`, marginTop:4 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'.07em', marginBottom:4 }}>Unlock with {nextTier.name}</div>
                  {nextTier.perks.slice(0,2).map((p,i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:8, fontSize:11, color:'var(--text3)', padding:'2px 0' }}>
                      <Lock size={10}/>{p}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Tier cards ── */}
        <div>
          <div className="rw-sec-head">
            <div className="rw-sec-title"><Crown size={15} color="var(--color-amber)"/>Membership Tiers</div>
          </div>
          <div className="rw-tier-row">
            {TIER_CONFIG.map(tier => (
              <TierCard key={tier.name} tier={tier} isCurrent={tier.name === curTier.name}/>
            ))}
          </div>
        </div>

      </div>
    </div>
  )
}